import React from 'react'
import './App.css'
import { Root, ContentContainer, Main, DisclaimerContainerV2 } from 'App.styles'
import { withRouter, RouteComponentProps, useLocation } from 'react-router-dom'
import { getRouteValue, RouteContext, RouteContextValue } from 'RouteContext'
import { useTranslation } from 'react-i18next'
import { Metrics } from './components/metrics/metrics'
import { LanguagePreferenceProvider, LopContext } from './util/language_preference_provider/language_preference_provider'
import { ReferrerContextProvider, ErrorBanner, DefaultExperimentsProvider } from '@amzn/aapn-react-components'
import { Text } from '@amzn/storm-ui'
import { useErrorOutage } from './util/error_outage/error_outage_hook'
import { ClickMetricsProvider } from './util/click_metrics_provider'
import { CrowdSurfIdentityContextProvider } from 'util/identity_provider'

import { ComparedPartnersContextProvider } from './views/directory/components/partner_comparison/compared_partners_context'
import { SearchRefContextProvider } from 'util/search_ref_provider/search_ref_provider'

const AppContent: React.FunctionComponent<React.PropsWithChildren<RouteComponentProps>> = (
  props: React.PropsWithChildren<RouteComponentProps>,
): JSX.Element => {
  const { t } = useTranslation()
  const location = useLocation()

  return (
    <LopContext.Consumer>
      {value => (
        <ContentContainer isRtl={value.lopRtl}>
          <Main>{props.children}</Main>
          {location.pathname !== '/partners/directory/error' && (
            <DisclaimerContainerV2>
              <Text>{t('APP:DISCLAIMER')}</Text>
            </DisclaimerContainerV2>
          )}
        </ContentContainer>
      )}
    </LopContext.Consumer>
  )
}

const App: React.FunctionComponent<React.PropsWithChildren<RouteComponentProps>> = (props: React.PropsWithChildren<RouteComponentProps>): JSX.Element => {
  const location = useLocation()
  const routeContextValue: RouteContextValue = { baseRoute: getRouteValue(location.pathname) }
  const { t } = useTranslation()
  const { data: errorOutageData, loading: loadingErrorOutageData } = useErrorOutage()

  const displayMainPage = (() => {
    if (!loadingErrorOutageData) {
      return (
        <ClickMetricsProvider>
          <Metrics {...props} />
          <AppContent {...props} />
        </ClickMetricsProvider>
      )
    }
  })()

  return (
    <Root>
      <SearchRefContextProvider>
        <RouteContext.Provider value={routeContextValue}>
          <ReferrerContextProvider>
            <DefaultExperimentsProvider principal={''}>
              <LanguagePreferenceProvider>
                <ComparedPartnersContextProvider>
                  {errorOutageData.isErrorOutage ? (
                    <LopContext.Consumer>
                      {value => (
                        <ContentContainer isRtl={value.lopRtl}>
                          <ErrorBanner errorMessage={t(errorOutageData.errorMessageToken)} instructionMessage={t(errorOutageData.instructionMessageToken)} />
                        </ContentContainer>
                      )}
                    </LopContext.Consumer>
                  ) : (
                    <CrowdSurfIdentityContextProvider>{displayMainPage}</CrowdSurfIdentityContextProvider>
                  )}
                </ComparedPartnersContextProvider>
              </LanguagePreferenceProvider>
            </DefaultExperimentsProvider>
          </ReferrerContextProvider>
        </RouteContext.Provider>
      </SearchRefContextProvider>
    </Root>
  )
}

const RouterCapableApp = withRouter(App)
export { RouterCapableApp as App }
